import React from 'react';
import { HashRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AnalysisProvider } from './contexts/AnalysisContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { IdentityRevealProvider } from './contexts/IdentityRevealContext';
/* Shared pages (v1 components reused in v2 layout) */
import { UploadPage } from './pages/UploadPage';
import { ProcessingPage } from './pages/ProcessingPage';
import { ResultsPage } from './pages/ResultsPage';
import { DeveloperPage } from './pages/DeveloperPage';
import { AgentPage } from './pages/AgentPage';
import { OrgPage } from './pages/OrgPage';
import { BillingPage } from './pages/BillingPage';
import { AdminPage } from './pages/AdminPage';
import { LoginPage } from './pages/LoginPage';
import { AdminGuard } from './admin/AdminGuard';
/* v2 imports */
import LayoutV2 from './components/layout/LayoutV2';
import { HomePage } from './pages/v2/HomePage';
import { JobsListPage } from './pages/v2/JobsListPage';
import { JobWorkspacePage } from './pages/v2/JobWorkspacePage';
import { CreateJobPage } from './pages/v2/CreateJobPage';
import { ResumesListPage } from './pages/v2/ResumesListPage';
import { ResumeViewPage } from './pages/v2/ResumeViewPage';
import { SettingsPage } from './pages/v2/SettingsPage';
import { DimensionStandardsPage } from './pages/v2/DimensionStandardsPage';
import './App.css';
import './styles/themes.css';
import './styles/dashboard.css';
import './components/layout/LayoutV2.css';

const DealDesk = React.lazy(() => import('./admin/DealDesk'));
const PricingAdmin = React.lazy(() => import('./admin/PricingAdmin'));
const MigrationCenter = React.lazy(() => import('./admin/MigrationCenter'));

/* v2 back-nav wrapper for settings sub-pages */
const SettingsSubPage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="v2-page">
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <button className="v2-btn v2-btn-ghost" onClick={() => navigate('/settings')}>
          ← Settings
        </button>
      </div>
      {children}
    </div>
  );
};

const ProtectedRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #4285f4', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <AnalysisProvider>
      <TenantProvider>
        <IdentityRevealProvider>
          <Router>
            <LayoutV2>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/jobs" element={<JobsListPage />} />
                <Route path="/jobs/new" element={<CreateJobPage />} />
                <Route path="/jobs/:jobId" element={<JobWorkspacePage />} />
                <Route path="/talent" element={<ResumesListPage />} />
                <Route path="/talent/:resumeId" element={<ResumeViewPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {/* Shared pages (v1 components reused in v2 layout) */}
                <Route path="/quick-screen" element={<UploadPage />} />
                <Route path="/processing" element={<ProcessingPage />} />
                <Route path="/results/:sessionId" element={<ResultsPage />} />
                <Route path="/agent" element={<AgentPage />} />
                <Route path="/organization" element={<SettingsSubPage><OrgPage /></SettingsSubPage>} />
                <Route path="/billing" element={<SettingsSubPage><BillingPage /></SettingsSubPage>} />
                <Route path="/settings/dimension-standards" element={<SettingsSubPage><DimensionStandardsPage /></SettingsSubPage>} />
                <Route path="/developer" element={<SettingsSubPage><DeveloperPage /></SettingsSubPage>} />
                <Route path="/admin" element={<AdminGuard><AdminPage /></AdminGuard>} />
                <Route
                  path="/admin/deal-desk"
                  element={
                    <AdminGuard>
                      <React.Suspense fallback={<div style={{ padding: 32 }}>Loading Deal Desk…</div>}>
                        <DealDesk />
                      </React.Suspense>
                    </AdminGuard>
                  }
                />
                <Route
                  path="/admin/pricing"
                  element={
                    <AdminGuard>
                      <React.Suspense fallback={<div style={{ padding: 32 }}>Loading Pricing Admin…</div>}>
                        <PricingAdmin />
                      </React.Suspense>
                    </AdminGuard>
                  }
                />
                <Route
                  path="/admin/migration-center"
                  element={
                    <AdminGuard>
                      <React.Suspense fallback={<div style={{ padding: 32 }}>Loading Migration Center…</div>}>
                        <MigrationCenter />
                      </React.Suspense>
                    </AdminGuard>
                  }
                />
              </Routes>
            </LayoutV2>
          </Router>
        </IdentityRevealProvider>
      </TenantProvider>
    </AnalysisProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProtectedRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;